/**
 * Joining a table from a phone: code, table, specialty, name.
 * QR codes on the projector open /join?code=…&table=… so most people only type a name.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { Role } from '@nightshift/engine/client';
import { api, linkValid, loadSeat, saveSeat, type SavedSeat } from '../net/api.ts';
import { Art, ROLE_TITLE } from '../ui/bits.tsx';
import { linkProps, navigate } from './router.ts';

interface JoinInfo {
  id: string;
  name: string;
  phase: string;
  tables: { no: number; seats: { role: Role; taken: boolean; bot: boolean; exists: boolean }[] }[];
}

const ROLE_LINE: Record<Role, string> = {
  EM: 'Front door. Patients arrive with you.',
  SURG: 'Surgical ward and Theatre.',
  PHYS: 'Medical ward and physician opinions.',
  PSYCH: 'Quiet Wing. Optional fifth player.',
  ICU: 'ICU, anaesthetics and Support.',
};

/** Offer the install affordance supplied by this browser. */
function InstallHint(): ReactNode {
  const [prompt, setPrompt] = useState<(Event & { prompt(): Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }> }) | null>(null);
  const [error, setError] = useState('');
  const standalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && navigator.standalone === true);
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  useEffect(() => {
    if (standalone) return;
    const receive = (event: Event): void => { event.preventDefault(); setPrompt(event as NonNullable<typeof prompt>); };
    const installed = (): void => setPrompt(null);
    window.addEventListener('beforeinstallprompt', receive);
    window.addEventListener('appinstalled', installed);
    return () => { window.removeEventListener('beforeinstallprompt', receive); window.removeEventListener('appinstalled', installed); };
  }, [standalone]);
  const install = async (): Promise<void> => {
    const event = prompt;
    setPrompt(null);
    if (!event) return;
    try { await event.prompt(); } catch (cause) {
      console.warn('Nightshift installation failed:', cause);
      setError('Installation could not start. Use your browser’s install menu.');
    }
  };
  if (standalone || (!prompt && !ios && !error)) return null;
  return <div className="join-foot">
    {prompt && <button className="btn" onClick={() => void install()}>Install Nightshift</button>}
    {ios && <p className="muted">On iPhone or iPad, use Share → Add to Home Screen.</p>}
    {error && <p role="status">{error}</p>}
  </div>;
}

export function JoinScreen({ initialCode, initialTable }: { initialCode: string; initialTable: number | null }) {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [table, setTable] = useState<number | null>(initialTable);
  const [role, setRole] = useState<Role | null>(null);
  const [name, setName] = useState(() => loadSeat()?.name ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<SavedSeat | null>(null);

  // After picking a specialty, bring the name field into view (the role grid can push it below the fold).
  useEffect(() => {
    if (role) document.getElementById('name')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [role]);

  // Offer to rejoin a remembered seat if it is still valid.
  useEffect(() => {
    const s = loadSeat();
    if (!s) return;
    linkValid(s.token)
      .then((ok) => (ok ? setSaved(s) : saveSeat(null)))
      .catch(() => setSaved(s));
  }, []);

  /** Fetch the session's tables. `keepErr` refreshes seats without hiding an error just shown. */
  const lookUp = async (c: string, keepErr = false) => {
    if (!keepErr) setErr(null);
    setBusy(true);
    try {
      const j = await api<JoinInfo>(`/api/join/${encodeURIComponent(c.trim().toUpperCase())}`);
      setInfo(j);
      if (j.tables.length === 1) setTable(j.tables[0]!.no);
    } catch (e) {
      setErr((e as Error).message);
      setInfo(null);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (initialCode.length >= 6) void lookUp(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async () => {
    if (!info || !table || !role || !name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const prev = loadSeat();
      const r = await api<{ token: string; sessionId: string }>('/api/join', {
        body: { code: code.trim().toUpperCase(), table, role, name: name.trim(), token: prev && prev.sessionId === info.id ? prev.token : undefined },
      });
      saveSeat({ token: r.token, sessionId: r.sessionId, code: code.trim().toUpperCase(), table, role, name: name.trim() });
      navigate('/play');
    } catch (e) {
      setErr((e as Error).message);
      if (info) void lookUp(code, true); // refresh seat availability, keep the message
    } finally {
      setBusy(false);
    }
  };

  const seats = info?.tables.find((t) => t.no === table)?.seats ?? [];
  const started = info && info.phase !== 'SETUP';

  return (
    <main className="join">
      <header className="join-head">
        <Art name="hero" className="join-art" eager />
        <h1 className="display">My First Nightshift</h1>
        <p className="muted">A night in the department, played on your phone and out loud.</p>
      </header>
      <InstallHint />

      {saved && (
        <section className="join-card rejoin">
          <p>
            You were <strong>{ROLE_TITLE[saved.role as Role] ?? saved.role}</strong> at Table {saved.table}.
          </p>
          <button className="btn btn-primary btn-block" onClick={() => navigate('/play')} data-testid="rejoin">
            Go back to my seat
          </button>
        </section>
      )}

      <section className="join-card">
        <form
          className="field"
          onSubmit={(e) => {
            e.preventDefault();
            void lookUp(code);
          }}
        >
          <label htmlFor="code">Session code</label>
          <div className="row">
            <input
              id="code"
              className="input code-input mono"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="On the projector"
              data-testid="join-code"
            />
            <button className="btn" type="submit" disabled={busy || code.length < 4}>
              Find
            </button>
          </div>
        </form>

        {info && (
          <>
            <p className="join-session">
              <strong>{info.name}</strong>
              {started ? <span className="muted"> has started. You can take a seat a bot is keeping warm.</span> : null}
            </p>
            {info.tables.length > 1 && (
              <div className="field">
                <span className="field-label">Your table</span>
                <div className="table-pick">
                  {info.tables.map((t) => (
                    <button key={t.no} className="chip" aria-pressed={table === t.no} onClick={() => setTable(t.no)} data-testid={`table-${t.no}`}>
                      Table {t.no}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {table && (
              <div className="field">
                <span className="field-label">Your specialty</span>
                <div className="role-pick compact">
                  {(['EM', 'SURG', 'PHYS', 'ICU', 'PSYCH'] as Role[]).map((r) => {
                    const s = seats.find((x) => x.role === r);
                    const takenByPerson = !!s?.taken;
                    const closed = !!started && !s?.exists;
                    return (
                      <button key={r} className={`role-btn spec-${r}`} aria-pressed={role === r} disabled={takenByPerson || closed} onClick={() => setRole(r)} data-testid={`role-${r}`}>
                        <span className="role-btn-top">
                          <span className="spec-dot" />
                          <strong>{ROLE_TITLE[r]}</strong>
                          {takenByPerson && <span className="muted">taken</span>}
                          {s?.bot && !takenByPerson && <span className="muted">bot, you can take over</span>}
                        </span>
                        {role === r && <span className="role-btn-line">{ROLE_LINE[r]}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {table && role && (
              <form
                className="field"
                onSubmit={(e) => {
                  e.preventDefault();
                  void join();
                }}
              >
                <label htmlFor="name">Your first name</label>
                <input id="name" className="input" value={name} maxLength={40} autoComplete="given-name" onChange={(e) => setName(e.target.value)} data-testid="join-name" />
                <button className="btn btn-primary btn-block" type="submit" disabled={busy || !name.trim()} data-testid="join-go">
                  Sit down as {ROLE_TITLE[role]}
                </button>
              </form>
            )}
          </>
        )}
        {err && (
          <p className="form-error" role="alert">
            {err}
          </p>
        )}
      </section>
      <p className="join-foot">
        <a className="muted" {...linkProps('/control')}>
          Running the session? Open Control
        </a>
      </p>
    </main>
  );
}
